import { Channel } from 'lib-shared-types-channels';

import { searchApi } from 'apis/search';
import { getChannelFeatureFlagsStatus } from 'utils/featureFlagging/channelFeatureFlags';

import { channelAttributes } from './channels';

type UseAvailableChannelsResult = {
  /** The available channels enriched with attributes. */
  readonly availableChannels: ReadonlyArray<
    {
      /** The channel key. */
      readonly channel: Channel;
    } & (typeof channelAttributes)[Channel]
  >;
};

/**
 * A hook that fetches and filters the available channels for the current
 * organization. Channels are eagerly fetched from the Search API on first
 * invocation and filtered by feature flags and known channel attributes.
 */
export const useAvailableChannels = (): UseAvailableChannelsResult => {
  const channelFeatureFlags = getChannelFeatureFlagsStatus();

  const { availableChannels } = searchApi.useReadSearchChannelsQuery(
    { scope: 'channels' },
    {
      selectFromResult: ({ data }) => {
        const apiChannels = new Set(
          data?.data.results.map(({ result }) => result) ?? [],
        );

        return {
          availableChannels: (Object.keys(channelAttributes) as Channel[])
            .filter(
              (channel) =>
                apiChannels.has(channel) && channelFeatureFlags.get(channel),
            )
            .map((channel) => ({
              channel,
              ...channelAttributes[channel],
            })),
        };
      },
    },
  );

  return { availableChannels };
};
